import React from 'react';
import { Check, Ruler } from 'lucide-react';
import { AppSelectDropdown } from '../../../../components/AppSelectDropdown';
import { SettingsToggle } from '../SettingsToggle';
import {
  DIN5008_RECOMMENDED_PAGE_MARGINS_PT,
  PDF_MM_TO_PT,
  layoutPaymentQrAlign,
  layoutPaymentQrPlacement,
  layoutShowsPageCarryOver,
  layoutShowsPaymentQr,
  layoutShowsTitleTotalsSummary,
  layoutTablePlacement,
  resolveDocumentPageMargins,
  TABLE_START_MIN_POSITIONS,
  type DocumentLayoutPaymentQrAlign,
  type DocumentLayoutPaymentQrPlacement,
  type DocumentLayoutTablePlacement,
  type DocumentLayoutV1,
  type DocumentPageMarginsPt,
} from '../../utils/documentLayoutTypes';
import { layoutShowsFoldMarks } from '../../utils/documentFoldMarks';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../../utils/settingsSectionStyles';

export type PageSettingsPatch = {
  pageMarginsPt?: DocumentPageMarginsPt;
  foldMarks?: boolean;
  pageCarryOver?: boolean;
  titleTotalsSummary?: boolean;
  paymentQr?: boolean;
  paymentQrPlacement?: DocumentLayoutPaymentQrPlacement;
  paymentQrAlign?: DocumentLayoutPaymentQrAlign;
  tablePlacement?: DocumentLayoutTablePlacement;
};

type Props = {
  layout: DocumentLayoutV1;
  onChange: (patch: PageSettingsPatch) => void;
};

const MIN_MARGIN_MM = 5;
const MAX_MARGIN_MM = 60;

function ptToMm(pt: number): number {
  return pt / PDF_MM_TO_PT;
}

function marginLabel(valuePt: number): string {
  return ptToMm(valuePt).toLocaleString('de-DE', {
    minimumFractionDigits: Number.isInteger(ptToMm(valuePt)) ? 0 : 1,
    maximumFractionDigits: 1,
  });
}

function sameMargins(a: DocumentPageMarginsPt, b: Readonly<DocumentPageMarginsPt>): boolean {
  return (['top', 'right', 'bottom', 'left'] as const).every(
    (side) => Math.abs(a[side] - b[side]) < 0.2,
  );
}

function MarginInput({
  label,
  valuePt,
  onCommit,
}: {
  label: string;
  valuePt: number;
  onCommit: (valuePt: number) => void;
}) {
  const [draft, setDraft] = React.useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    const parsed = Number(draft.trim().replace(',', '.'));
    setDraft(null);
    if (!Number.isFinite(parsed)) return;
    const mm = Math.min(MAX_MARGIN_MM, Math.max(MIN_MARGIN_MM, parsed));
    onCommit(Math.round(mm * PDF_MM_TO_PT * 10) / 10);
  };

  return (
    <label>
      <span className="mb-1 block text-[11px] font-medium text-app-text-subtle">{label}</span>
      <span className="relative block">
        <input
          type="text"
          inputMode="decimal"
          className={`${SETTINGS_INPUT_CLASS} pr-9 tabular-nums`}
          value={draft ?? marginLabel(valuePt)}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
            if (event.key === 'Escape') setDraft(null);
          }}
          aria-label={`${label} Seitenrand in Millimetern`}
        />
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-app-text-subtle">
          mm
        </span>
      </span>
    </label>
  );
}

export function PageSettingsPanel({ layout, onChange }: Props) {
  const margins = resolveDocumentPageMargins(layout);
  const usesDinRecommendation = sameMargins(margins, DIN5008_RECOMMENDED_PAGE_MARGINS_PT);
  const foldMarksOn = layoutShowsFoldMarks(layout);
  const pageCarryOverOn = layoutShowsPageCarryOver(layout);
  const titleTotalsSummaryOn = layoutShowsTitleTotalsSummary(layout);
  const tablePlacement = layoutTablePlacement(layout);
  const paymentQrOn = layoutShowsPaymentQr(layout);
  const paymentQrPlacement = layoutPaymentQrPlacement(layout);
  const paymentQrAlign = layoutPaymentQrAlign(layout);

  const patch = (side: keyof DocumentPageMarginsPt, valuePt: number) => {
    onChange({ pageMarginsPt: { ...margins, [side]: valuePt } });
  };

  return (
    <aside className="flex w-[298px] shrink-0 flex-col overflow-hidden self-stretch border-l border-app-border bg-app-surface">
      <div className="border-b border-app-border px-4 py-4">
        <h2 className="text-sm font-semibold text-app-text">Seiteneinstellungen</h2>
        <p className="mt-0.5 text-xs text-app-text-subtle">Schreibbereich, Ränder und Dokumentfluss</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-8 app-scrollbar">
        <section>
          <p className={SETTINGS_FIELD_LABEL}>Ausrichtung</p>
          <button
            type="button"
            className={`${SETTINGS_BTN_OUTLINE} mt-2 w-full justify-between ${
              usesDinRecommendation ? 'border-pipedrive-blue bg-blue-50 text-pipedrive-blue' : ''
            }`}
            onClick={() => onChange({ pageMarginsPt: { ...DIN5008_RECOMMENDED_PAGE_MARGINS_PT } })}
            aria-pressed={usesDinRecommendation}
          >
            <span className="inline-flex items-center gap-2">
              <Ruler className="h-4 w-4" />
              DIN 5008 – empfohlen
            </span>
            {usesDinRecommendation ? <Check className="h-4 w-4" /> : null}
          </button>
          <p className="mt-2 text-[11px] leading-relaxed text-app-text-subtle">
            Links 25 mm, rechts 20 mm, oben und unten 20 mm. Der 45-mm-Briefkopf
            der Form B wird weiterhin über den Kopfbereich gestaltet.
          </p>
        </section>

        <div className="my-4 border-t border-app-border" />

        <section>
          <div>
            <p className={SETTINGS_FIELD_LABEL}>Seitenränder</p>
            <p className="mt-0.5 text-[11px] text-app-text-subtle">
              Eigene Werte wechseln automatisch zu einer individuellen Ausrichtung.
            </p>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <MarginInput label="Oben" valuePt={margins.top} onCommit={(value) => patch('top', value)} />
            <MarginInput label="Rechts" valuePt={margins.right} onCommit={(value) => patch('right', value)} />
            <MarginInput label="Unten" valuePt={margins.bottom} onCommit={(value) => patch('bottom', value)} />
            <MarginInput label="Links" valuePt={margins.left} onCommit={(value) => patch('left', value)} />
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-app-text-subtle">
            Kopf- und Fußbereich dürfen für Logo und Gestaltung über den Schreibbereich hinausragen.
            Ein größerer Fußbereich reserviert automatisch zusätzlichen Platz.
          </p>
        </section>

        <div className="my-4 border-t border-app-border" />

        <section>
          <label className="block space-y-1.5">
            <span className={SETTINGS_FIELD_LABEL}>Tabellenbeginn</span>
            <AppSelectDropdown
              id="table-placement"
              value={tablePlacement}
              onChange={(value) => onChange({ tablePlacement: value as DocumentLayoutTablePlacement })}
              options={[
                { value: 'flow', label: 'Direkt nach dem Anfangstext' },
                { value: 'auto', label: 'Automatisch' },
                { value: 'page2', label: 'Immer auf Seite 2' },
              ]}
              className="w-full"
              triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
            />
          </label>
          <p className="mt-2 text-[11px] leading-relaxed text-app-text-subtle">
            {tablePlacement === 'auto'
              ? `Die Tabelle folgt dem Anfangstext. Passen Tabellenkopf und die ersten ${TABLE_START_MIN_POSITIONS} Positionen nicht mehr auf die Seite, beginnt sie auf einer neuen Seite.`
              : tablePlacement === 'page2'
                ? 'Die Tabelle beginnt immer auf der zweiten Seite. Anfangstext und Bilder bleiben auf Seite 1.'
                : 'Die Tabelle folgt direkt dem Anfangstext, auch wenn sie unten auf der Seite beginnt.'}
          </p>
        </section>

        <div className="my-4 border-t border-app-border" />

        <section>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={SETTINGS_FIELD_LABEL}>Falthilfe</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-app-text-subtle">
                Falz- und Lochmarken nach DIN 5008 Form B (105 / 148,5 / 210 mm) im linken Heftrand
                für DIN-lang-Fensterumschläge.
              </p>
            </div>
            <SettingsToggle
              active={foldMarksOn}
              label="Falthilfe"
              onClick={() => onChange({ foldMarks: !foldMarksOn })}
            />
          </div>
        </section>

        <div className="my-4 border-t border-app-border" />

        <section>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={SETTINGS_FIELD_LABEL}>Übertrag</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-app-text-subtle">
                Bei Seitenwechsel laufende Nettosumme und Tabellenkopf auf Folgeseiten (nur im PDF).
              </p>
            </div>
            <SettingsToggle
              active={pageCarryOverOn}
              label="Übertrag"
              onClick={() => onChange({ pageCarryOver: !pageCarryOverOn })}
            />
          </div>
        </section>

        <div className="my-4 border-t border-app-border" />

        <section>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={SETTINGS_FIELD_LABEL}>Titelsummen</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-app-text-subtle">
                Übersicht aller Titelgruppen-Summen vor dem Nettobetrag.
              </p>
            </div>
            <SettingsToggle
              active={titleTotalsSummaryOn}
              label="Titelsummen"
              onClick={() => onChange({ titleTotalsSummary: !titleTotalsSummaryOn })}
            />
          </div>
        </section>

        <div className="my-4 border-t border-app-border" />

        <section>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={SETTINGS_FIELD_LABEL}>Zahlungs-QR / GiroCode</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-app-text-subtle">
                EPC-QR auf Rechnungen und Mahnungen. Banking-Apps übernehmen Empfänger, IBAN, Betrag
                und Rechnungsnummer.
              </p>
            </div>
            <SettingsToggle
              active={paymentQrOn}
              label="Zahlungs-QR / GiroCode"
              onClick={() => onChange({ paymentQr: !paymentQrOn })}
            />
          </div>
          {paymentQrOn ? (
            <div className="mt-3 space-y-3">
              <label className="block space-y-1.5">
                <span className="text-[11px] font-medium text-app-text-subtle">Position</span>
                <AppSelectDropdown
                  id="payment-qr-placement"
                  value={paymentQrPlacement}
                  onChange={(value) =>
                    onChange({ paymentQrPlacement: value as DocumentLayoutPaymentQrPlacement })
                  }
                  options={[
                    { value: 'totals', label: 'Neben Summen' },
                    { value: 'afterGreeting', label: 'Nach Schlusssatz und Grußformel' },
                  ]}
                  className="w-full"
                  triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                />
              </label>
              {paymentQrPlacement === 'afterGreeting' ? (
                <label className="block space-y-1.5">
                  <span className="text-[11px] font-medium text-app-text-subtle">Ausrichtung</span>
                  <AppSelectDropdown
                    id="payment-qr-align"
                    value={paymentQrAlign}
                    onChange={(value) =>
                      onChange({ paymentQrAlign: value as DocumentLayoutPaymentQrAlign })
                    }
                    options={[
                      { value: 'left', label: 'Links' },
                      { value: 'center', label: 'Mittig' },
                    ]}
                    className="w-full"
                    triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                  />
                </label>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>
    </aside>
  );
}
